"use client";

import Lenis from 'lenis';
import type LenisType from 'lenis';
import { useRef, useState } from 'react';
import React, { useEffect } from 'react';
import { LenisContext } from '@/context/lenis-context';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/all';
import ToastContainer, { ToastType } from '@/components/ToastHandler';
import NProgress from 'nprogress';
import { usePathname } from 'next/navigation';

NProgress.configure({
  showSpinner: false,
});

function startNavigationProgress()
{
  if (!NProgress.isStarted())
    NProgress.start();
}

function isInternalPageNavigation(anchor: HTMLAnchorElement): boolean
{
  if (anchor.target && anchor.target !== '_self')
    return false;

  if (anchor.hasAttribute('download'))
    return false;

  const href = anchor.getAttribute('href');

  if (!href || href.startsWith('#'))
    return false;

  let targetUrl: URL;
  try
  {
    targetUrl = new URL(anchor.href, window.location.href);
  }
  catch
  {
    return false;
  }

  return targetUrl.origin === window.location.origin
    && targetUrl.pathname !== window.location.pathname;
}

export default function LenisProvider({
  children,
  options,
  ...props
}:
  {
    children: React.ReactNode;
    options?: any;
  })
{

  // navigation
  const pathname = usePathname();


  // state
  const [lenisInstance, setLenisInstance] = useState<LenisType | null>(null);
  const [toasts, setToasts] = useState<ToastType[]>([]);

  const removeToastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigationPathRef = useRef(pathname);

  useEffect(() =>
  {
    const lenis = new Lenis();

    setLenisInstance(lenis);

    gsap.registerPlugin(ScrollTrigger);

    lenis.on('scroll', ScrollTrigger.update);


    gsap.ticker.add((time: number) =>
    {
      lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    /* 
    If window is resized or if the document body 
    grows from an accordion we need to manually refresh the scrolltrigger
    */
    let resizeTimeout: NodeJS.Timeout | null = null;
    let resizeObserver = new ResizeObserver(() =>
    {
      resizeTimeout && clearTimeout(resizeTimeout);

      resizeTimeout = setTimeout(() =>
      {
        ScrollTrigger.refresh();
      }, 10);
    });
    resizeObserver.observe(document.body);

    return () =>
    {
      lenis.destroy();
    };

  }, []);

  useEffect(() =>
  {
    navigationPathRef.current = pathname;
    NProgress.done();
  }, [pathname]);

  useEffect(() =>
  {
    const handleDocumentClick = (event: MouseEvent) =>
    {
      if (
        event.defaultPrevented
        || event.button !== 0
        || event.metaKey
        || event.ctrlKey
        || event.shiftKey
        || event.altKey
      )
        return;

      const target = event.target;
      if (!(target instanceof Element))
        return;

      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement) || !isInternalPageNavigation(anchor))
        return;

      startNavigationProgress();
    };

    document.addEventListener('click', handleDocumentClick, true);

    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, []);

  useEffect(() =>
  {
    const handlePopState = () =>
    {
      if (window.location.pathname !== navigationPathRef.current)
        startNavigationProgress();
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);


  // queue deletions
  useEffect(() =>
  {
    // still ensures if handle remove toast was called manually we still remove the latest timeout
    removeToastTimeoutRef.current && clearTimeout(removeToastTimeoutRef.current);
    removeToastTimeoutRef.current = setTimeout(() =>
    {
      setToasts((prev) =>
      {
        return [...prev.slice(1)];
      });
    }, 5000);

  }, [toasts]);


  function HandleAddToast(toast: ToastType)
  {
    setToasts((prev) =>
    {
      return [...prev, toast];
    });
  }

  function HandleRemoveToast(id: string)
  {
    setToasts((prev) =>
    {
      return prev.filter((toast) => toast.id !== id);
    });
  }

  function HandleInitializeToasts(toasts: ToastType[])
  {
    setToasts(toasts);
  }

  return (
    <LenisContext.Provider
      value={ {
        lenisInstance,
        requestToasts: toasts,
        startLoader: startNavigationProgress,
        initializeToasts: HandleInitializeToasts,
        addToast: HandleAddToast,
        removeToast: HandleRemoveToast,
        scrollToElement: (element: HTMLElement, offset?: number) =>
        {
          lenisInstance?.scrollTo(element, {
            offset: offset || 0,
          });
        }
      } }
      { ...props }>
      <ToastContainer />
      { children }
    </LenisContext.Provider>
  );
}
